import React, { useEffect, useRef, useState } from "react";
import { MapPin, Globe, AlertTriangle, Upload, FileText } from "lucide-react";
import { theme, floatingSheet, countTag, btnPrimary, btnSecondary, inputStyle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { runDocumentJob } from "../lib/generatePolicy";
import { MarkdownDocument } from "./MarkdownDocument";
import { PortalLink } from "./PortalLink";
import { COUNTRIES } from "../data/countriesData";
import { fmtBytes } from "../utils/format";

// Policy addenda — local supplements to a policy, each linked to the policy it
// supplements and tagged with the sites (offices) and / or countries in which
// it applies. Rendered inside the expanded policy card on the Policy Templates
// page; the site detail page lists the same rows for one site. The tags are a
// scope for readers and auditors, never access control.

export interface AddendumVersion {
  id: string;
  versionNumber: number;
  source: "generated" | "upload";
  fileName: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  parentDocumentId: string | null;
  createdAt: string;
}

export type AddendumFreshness =
  | { state: "none" }
  | { state: "current" }
  | { state: "policy_missing" }
  | { state: "policy_changed"; policyVersion: number }
  | { state: "uploaded" };

export interface PolicyAddendum {
  id: string;
  templateId: string;
  templateTitle: string;
  title: string;
  countries: string[];
  sites: Array<{ id: string; name: string; country: string; kind: string }>;
  effectiveCountries: string[];
  scopeLabel: string;
  scopeNotes: string | null;
  latestVersion: AddendumVersion | null;
  versionCount: number;
  freshness: AddendumFreshness;
  policyVersion: { id: string; versionNumber: number } | null;
  createdAt: string;
  updatedAt: string;
}

interface AddendumOptions {
  sites: Array<{ id: string; name: string; kind: string; country: string; city: string | null; inScope: boolean }>;
  countries: string[];
}

const ACCEPT = ".pdf,.docx,.doc,.md,.txt,.odt,.rtf";

const chip = (color: string, bg: string): React.CSSProperties => ({
  display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 600, padding: "2px 7px",
  borderRadius: 2, color, background: bg, border: `1px solid ${theme.color.border}`, whiteSpace: "nowrap",
});
const smallBtn: React.CSSProperties = {
  padding: "5px 12px", borderRadius: 3, fontSize: 11, fontWeight: 600, cursor: "pointer",
  border: `1px solid ${theme.color.borderStrong}`, background: "transparent", color: theme.color.muted,
};
const smallBtnAccent: React.CSSProperties = { ...smallBtn, border: `1px solid ${theme.color.accent}`, background: theme.color.accentBg, color: theme.color.accentText };

export function freshnessBadge(f: AddendumFreshness): { label: string; color: string; bg: string; warn?: boolean } | null {
  switch (f.state) {
    case "none":           return { label: "Not generated", color: theme.color.faint, bg: theme.color.panelAlt };
    case "current":        return { label: "Current", color: theme.color.successText, bg: theme.color.successBg };
    case "policy_changed": return { label: `Policy regenerated (v${f.policyVersion}) — review`, color: theme.color.warningText, bg: theme.color.warningBg, warn: true };
    case "policy_missing": return { label: "Policy version removed — review", color: theme.color.warningText, bg: theme.color.warningBg, warn: true };
    case "uploaded":       return { label: "Uploaded document", color: theme.color.accentText, bg: theme.color.accentBg };
  }
}

/** Site and country chips for one addendum — shared with the site detail page. */
export function AddendumScopeChips({ addendum, linkSites = false }: { addendum: Pick<PolicyAddendum, "sites" | "countries">; linkSites?: boolean }) {
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {addendum.sites.map((s) => {
        const c = <span key={s.id} title={`${s.name} · ${s.country}`} style={chip(theme.color.accentText, theme.color.accentBg)}><MapPin size={10} />{s.name}</span>;
        return linkSites ? <PortalLink key={s.id} to={`/sites/${s.id}`} style={{ textDecoration: "none" }}>{c}</PortalLink> : c;
      })}
      {addendum.countries.map((c) => (
        <span key={c} title="Applies in this jurisdiction" style={chip(theme.color.muted, theme.color.panelAlt)}><Globe size={10} />{c}</span>
      ))}
    </div>
  );
}

// ── Create / edit modal ───────────────────────────────────────────────────────

function AddendumModal({ templateId, templateTitle, initial, onClose, onSaved }: {
  templateId: string;
  templateTitle: string;
  initial?: PolicyAddendum;
  onClose: () => void;
  onSaved: (a: PolicyAddendum) => void;
}) {
  const [options, setOptions]   = useState<AddendumOptions | null>(null);
  const [title, setTitle]       = useState(initial?.title ?? "");
  const [siteIds, setSiteIds]   = useState<string[]>(initial?.sites.map((s) => s.id) ?? []);
  const [countries, setCountries] = useState<string[]>(initial?.countries ?? []);
  const [otherCountry, setOtherCountry] = useState("");
  const [scopeNotes, setScopeNotes] = useState(initial?.scopeNotes ?? "");
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");

  useEffect(() => {
    api.get<AddendumOptions>("/policy-addenda/options").then(setOptions).catch(() => setOptions({ sites: [], countries: [] }));
  }, []);

  const toggle = (list: string[], value: string, set: (v: string[]) => void) =>
    set(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);

  // The organisation's own countries first; any other country can be added below.
  const offeredCountries = Array.from(new Set([...(options?.countries ?? []), ...countries])).sort();
  const otherCountries = COUNTRIES.filter((c) => !offeredCountries.includes(c));

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!title.trim()) { setError("Give the addendum a title."); return; }
    if (siteIds.length === 0 && countries.length === 0) { setError("Tag the addendum with at least one site or one country."); return; }
    setSaving(true);
    try {
      const body = { title: title.trim(), siteIds, countries, scopeNotes: scopeNotes.trim() || null };
      const result = initial
        ? await api.patch<{ addendum: PolicyAddendum }>(`/policy-addenda/${initial.id}`, body)
        : await api.post<{ addendum: PolicyAddendum }>("/policy-addenda", { templateId, ...body });
      onSaved(result.addendum);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  const overlay: React.CSSProperties = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16,
  };
  const labelStyle: React.CSSProperties = { fontSize: 11, color: theme.color.subtle, fontWeight: 600 };
  const hintStyle: React.CSSProperties = { fontSize: 11, color: theme.color.subtle, lineHeight: 1.4 };
  const checkRow: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: theme.color.text, cursor: "pointer" };

  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...floatingSheet, padding: 20, width: "100%", maxWidth: 600, maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6, paddingBottom: 10, borderBottom: "1px solid var(--c-line)" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>{initial ? "Edit addendum" : "New addendum"}</div>
          <button type="button" onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.muted, fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ fontSize: 12, color: theme.color.subtle, marginBottom: 16 }}>Supplements <strong style={{ color: theme.color.text }}>{templateTitle}</strong>, which continues to apply in full everywhere.</div>

        <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={labelStyle}>Title</label>
            <input style={inputStyle} value={title} autoFocus required maxLength={120} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Germany — works council and BDSG supplement" />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={labelStyle}>Applies at these sites</label>
            {options === null && <div style={hintStyle}>Loading sites…</div>}
            {options !== null && options.sites.length === 0 && (
              <div style={hintStyle}>No sites are recorded yet. Add offices under <PortalLink to="/sites" style={{ color: theme.color.accentText }}>Sites &amp; locations</PortalLink>, or tag the addendum by country below.</div>
            )}
            {options?.sites.map((s) => (
              <label key={s.id} style={checkRow}>
                <input type="checkbox" checked={siteIds.includes(s.id)} onChange={() => toggle(siteIds, s.id, setSiteIds)} />
                <span>{s.name}</span>
                <span style={{ fontSize: 11, color: theme.color.subtle }}>{[s.city, s.country].filter(Boolean).join(", ")}{s.inScope ? "" : " · out of scope"}</span>
              </label>
            ))}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <label style={labelStyle}>Applies in these jurisdictions</label>
            <div style={hintStyle}>A tagged site already brings its own country into the addendum; tick a country here when the addendum applies to everything the organisation does there, whichever office.</div>
            {offeredCountries.map((c) => (
              <label key={c} style={checkRow}>
                <input type="checkbox" checked={countries.includes(c)} onChange={() => toggle(countries, c, setCountries)} />
                <span>{c}</span>
              </label>
            ))}
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <select style={{ ...inputStyle, maxWidth: 280 }} value={otherCountry} onChange={(e) => setOtherCountry(e.target.value)}>
                <option value="">Add another country…</option>
                {otherCountries.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button type="button" style={smallBtn} disabled={!otherCountry} onClick={() => { if (otherCountry) { setCountries([...countries, otherCountry]); setOtherCountry(""); } }}>Add</button>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <label style={labelStyle}>What the addendum must cover (optional)</label>
            <textarea style={{ ...inputStyle, minHeight: 90, resize: "vertical", fontFamily: theme.font }} value={scopeNotes} maxLength={4000} onChange={(e) => setScopeNotes(e.target.value)}
              placeholder="Works council agreement of March 2024 on monitoring; landlord requires escorted access after 19:00; local language version needed…" />
            <div style={hintStyle}>Given to the AI alongside the parent policy, the sites' details and the laws of the tagged jurisdictions.</div>
          </div>

          {error && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} style={btnSecondary}>Cancel</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>{saving ? "Saving…" : initial ? "Save" : "Create addendum"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── One addendum ──────────────────────────────────────────────────────────────

function AddendumRow({ addendum, policyGenerated, canWrite, canGenerate, onChanged, onDeleted }: {
  addendum: PolicyAddendum;
  policyGenerated: boolean;
  canWrite: boolean;
  canGenerate: boolean;
  onChanged: (a: PolicyAddendum) => void;
  onDeleted: (id: string) => void;
}) {
  const [open, setOpen]           = useState(false);
  const [content, setContent]     = useState<string | null | undefined>(undefined); // undefined = not loaded, null = uploaded file
  const [busy, setBusy]           = useState<"generate" | "upload" | "delete" | null>(null);
  const [error, setError]         = useState("");
  const [editing, setEditing]     = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const latest = addendum.latestVersion;
  const badge = freshnessBadge(addendum.freshness);

  async function loadContent() {
    if (!latest) return;
    try {
      const data = await api.get<{ content: string | null }>(`/policy-addenda/${addendum.id}/content`);
      setContent(data.content);
    } catch (err) {
      setError(apiErrorMessage(err, "Could not load the addendum"));
    }
  }

  function toggleOpen() {
    const next = !open;
    setOpen(next);
    if (next && content === undefined) void loadContent();
  }

  async function generate() {
    setBusy("generate"); setError("");
    try {
      const data = await runDocumentJob<{ addendum: PolicyAddendum; content: string }>(`/policy-addenda/${addendum.id}/generate`);
      setContent(data.content);
      setOpen(true);
      onChanged(data.addendum);
    } catch (err) {
      setError(apiErrorMessage(err, "Generation failed. Please try again."));
    } finally {
      setBusy(null);
    }
  }

  async function upload(file: File) {
    setBusy("upload"); setError("");
    try {
      const contentType = file.type || "application/octet-stream";
      const { uploadUrl, blobPath, maxBytes } = await api.post<{ uploadUrl: string; blobPath: string; maxBytes: number }>(`/policy-addenda/${addendum.id}/request-upload`, { fileName: file.name, contentType });
      if (file.size > maxBytes) throw new Error(`The file must be under ${Math.round(maxBytes / 1024 / 1024)} MB.`);
      const put = await fetch(uploadUrl, { method: "PUT", body: file, headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": contentType } });
      if (!put.ok) throw new Error("Upload failed. Please try again.");
      const data = await api.post<{ addendum: PolicyAddendum }>(`/policy-addenda/${addendum.id}/versions`, { blobPath, fileName: file.name });
      setContent(null);
      onChanged(data.addendum);
    } catch (err) {
      setError(apiErrorMessage(err, "Upload failed. Please try again."));
    } finally {
      setBusy(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  async function download() {
    if (!latest) return;
    try {
      const data = await api.get<{ url: string }>(`/policy-addenda/versions/${latest.id}/download-url`);
      const a = document.createElement("a");
      a.href = data.url; a.download = ""; a.click();
    } catch (err) {
      setError(apiErrorMessage(err, "Download failed"));
    }
  }

  async function remove() {
    const versions = addendum.versionCount === 0 ? "" : addendum.versionCount === 1 ? " and its one version" : ` and all ${addendum.versionCount} versions`;
    if (!window.confirm(`Delete "${addendum.title}"${versions}?`)) return;
    setBusy("delete"); setError("");
    try {
      await api.del(`/policy-addenda/${addendum.id}`);
      onDeleted(addendum.id);
    } catch (err) {
      setError(apiErrorMessage(err, "Delete failed"));
      setBusy(null);
    }
  }

  return (
    <div style={{ border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "10px 12px", background: theme.color.panelAlt }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.heading, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {addendum.title}
            {badge && <span style={chip(badge.color, badge.bg)}>{badge.warn && <AlertTriangle size={10} />}{badge.label}</span>}
          </div>
          <div style={{ marginTop: 6 }}><AddendumScopeChips addendum={addendum} linkSites /></div>
          {latest && (
            <div style={{ fontSize: 11, color: theme.color.faint, marginTop: 6 }}>
              v{latest.versionNumber} · {latest.source === "upload" ? `uploaded ${latest.fileName ?? "file"}${latest.sizeBytes ? ` (${fmtBytes(latest.sizeBytes)})` : ""}` : "generated"} · {new Date(latest.createdAt).toLocaleDateString("en-GB")}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {latest && <button type="button" style={smallBtn} onClick={toggleOpen}>{open ? "Hide" : latest.source === "upload" ? "Details" : "View"}</button>}
          {latest && <button type="button" style={smallBtn} onClick={download}>Download</button>}
          {canGenerate && (
            <button type="button" style={smallBtnAccent} disabled={busy !== null || !policyGenerated} onClick={generate}
              title={policyGenerated ? "" : "Generate the policy first — an addendum supplements a policy that exists."}>
              {busy === "generate" ? "Generating…" : latest ? "Regenerate" : "Generate"}
            </button>
          )}
          {canWrite && (
            <>
              <button type="button" style={smallBtn} disabled={busy !== null} onClick={() => fileInput.current?.click()} title="Upload an existing addendum as the next version">
                <Upload size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />{busy === "upload" ? "Uploading…" : "Upload"}
              </button>
              <input ref={fileInput} type="file" accept={ACCEPT} style={{ display: "none" }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} />
              <button type="button" style={smallBtn} disabled={busy !== null} onClick={() => setEditing(true)}>Edit</button>
              <button type="button" style={{ ...smallBtn, color: theme.color.dangerText }} disabled={busy !== null} onClick={remove}>{busy === "delete" ? "Deleting…" : "Delete"}</button>
            </>
          )}
        </div>
      </div>

      {busy === "generate" && (
        <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 8 }}>
          Writing the addendum against {addendum.templateTitle}{addendum.policyVersion ? ` v${addendum.policyVersion.versionNumber}` : ""}, the tagged sites and the laws of {addendum.effectiveCountries.join(", ") || "the tagged jurisdictions"} — usually one to three minutes. You can leave this page; the version will be here when you come back.
        </div>
      )}
      {error && <div style={{ fontSize: 11, color: theme.color.dangerText, marginTop: 8 }}>{error}</div>}

      {open && latest && (
        <div style={{ marginTop: 10 }}>
          {content === undefined && <div style={{ fontSize: 11, color: theme.color.subtle }}>Loading…</div>}
          {content === null && (
            <div style={{ fontSize: 12, color: theme.color.subtle, display: "flex", alignItems: "center", gap: 6 }}>
              <FileText size={13} />An uploaded document has no preview here — download it to read it. Regenerating writes the next version from its text and the current policy.
            </div>
          )}
          {typeof content === "string" && (
            <>
              <div style={{ background: theme.color.panel, border: `1px solid ${theme.color.border}`, borderRadius: 3, padding: "14px 16px", maxHeight: 420, overflowY: "auto" }}>
                <MarkdownDocument content={content} size={12} />
              </div>
              <div style={{ fontSize: 10, color: theme.color.faint, marginTop: 6 }}>AI-generated — review with the parent policy before use in an audit or formal process.</div>
            </>
          )}
        </div>
      )}

      {editing && (
        <AddendumModal templateId={addendum.templateId} templateTitle={addendum.templateTitle} initial={addendum} onClose={() => setEditing(false)} onSaved={onChanged} />
      )}
    </div>
  );
}

// ── Section inside a policy card ─────────────────────────────────────────────

export function PolicyAddendaSection({ templateId, templateTitle, items, policyGenerated, canWrite, canGenerate, onChanged, onDeleted }: {
  templateId: string;
  templateTitle: string;
  items: PolicyAddendum[];
  policyGenerated: boolean;
  canWrite: boolean;
  canGenerate: boolean;
  onChanged: (a: PolicyAddendum) => void;
  onDeleted: (id: string) => void;
}) {
  const [creating, setCreating] = useState(false);

  return (
    <div style={{ borderTop: `1px solid ${theme.color.border}`, paddingTop: 12, marginTop: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: theme.color.heading }}>Addenda</div>
        <span style={countTag}>{items.length}</span>
        {canWrite && <button type="button" style={{ ...smallBtn, marginLeft: "auto" }} onClick={() => setCreating(true)}>+ Add addendum</button>}
      </div>
      {items.length === 0 ? (
        <div style={{ fontSize: 11, color: theme.color.subtle, lineHeight: 1.5 }}>
          No addendum yet. An addendum supplements this policy for particular offices or jurisdictions — local law, a works council agreement, a landlord's rules — and is tagged with the sites and countries it applies in.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {items.map((a) => (
            <AddendumRow key={a.id} addendum={a} policyGenerated={policyGenerated} canWrite={canWrite} canGenerate={canGenerate} onChanged={onChanged} onDeleted={onDeleted} />
          ))}
        </div>
      )}
      {creating && (
        <AddendumModal templateId={templateId} templateTitle={templateTitle} onClose={() => setCreating(false)} onSaved={onChanged} />
      )}
    </div>
  );
}
